import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * The app's own "Are you sure?", in place of window.confirm. Some browsers
 * (embedded ones, sandboxed frames, Chrome after "prevent this page from
 * creating additional dialogs") refuse the browser's dialog without showing
 * it, so the button looked dead. Call askInk from anywhere and await it:
 *
 *   if (!(await askInk({ title: 'Delete “Home”?', confirmLabel: 'Delete', danger: true }))) return;
 *
 * AppShell renders <InkConfirm />; a screen outside the shell gets one mounted
 * on first use. Escape, Cancel or a click outside answers no.
 */

export interface InkConfirmOptions {
  title: string;
  /** What happens, in a sentence or two. */
  body?: string;
  /** What's kept or can't be undone, set apart in the grey note box. */
  note?: string;
  /** Label · text rows under the body (Look and pages: Drafts, Menu, Form…). */
  rows?: Array<{ label: string; text: string }>;
  /** A reassuring last line in green ("Nothing is published."). */
  foot?: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** Red confirm button, and Cancel takes the focus: delete, disconnect, take down. */
  danger?: boolean;
  /**
   * Do the work here, with the question still open: the answer is yes once it
   * succeeds. If it throws, the dialog stays and says `failure`, ready to try again.
   */
  run?: { work: () => Promise<void>; busyLabel: string; failure: string; retryLabel: string };
}

interface Ask extends InkConfirmOptions {
  resolve: (ok: boolean) => void;
}
type Listener = (ask: Ask) => void;
const listeners = new Set<Listener>();
let pending: Ask | null = null;
let selfMounted = false;

export function askInk(options: InkConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const ask = { ...options, resolve };
    if (listeners.size) {
      listeners.forEach((fn) => fn(ask));
      return;
    }
    // Not in the shell (or not mounted yet): it shows as soon as a host is up.
    pending?.resolve(false);
    pending = ask;
    if (!selfMounted && typeof document !== 'undefined') {
      selfMounted = true;
      const el = document.createElement('div');
      document.body.appendChild(el);
      createRoot(el).render(<InkConfirm />);
    }
  });
}

export function InkConfirm() {
  const [ask, setAsk] = useState<Ask | null>(null);
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState(false);
  const askRef = useRef<Ask | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const confirmButton = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const fn: Listener = (next) => {
      // A second question replaces the first, which counts as a no.
      askRef.current?.resolve(false);
      returnFocus.current = document.activeElement as HTMLElement | null;
      askRef.current = next;
      setFailed(false);
      setAsk(next);
    };
    listeners.add(fn);
    if (pending) {
      fn(pending);
      pending = null;
    }
    return () => {
      listeners.delete(fn);
    };
  }, []);

  const answer = useCallback((ok: boolean) => {
    const current = askRef.current;
    askRef.current = null;
    setAsk(null);
    returnFocus.current?.focus?.();
    current?.resolve(ok);
  }, []);

  const confirm = async () => {
    const run = askRef.current?.run;
    if (!run) return answer(true);
    setRunning(true);
    setFailed(false);
    try {
      await run.work();
      answer(true);
    } catch {
      setFailed(true);
    } finally {
      setRunning(false);
    }
    return undefined;
  };

  // The button was disabled while it ran, which took the focus away from it.
  useEffect(() => {
    if (failed) confirmButton.current?.focus();
  }, [failed]);

  useEffect(() => {
    if (!ask) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (!running) answer(false);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [ask, answer, running]);

  if (!ask) return null;
  return (
    <div
      className="ink-modal-back ink-confirm-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !running) answer(false);
      }}
    >
      <div
        className="ink-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ink-confirm-title"
        aria-describedby={ask.body ? 'ink-confirm-copy' : undefined}
      >
        <div className="ink-modal-title" id="ink-confirm-title">
          {ask.title}
        </div>
        {ask.body ? (
          <div className="ink-modal-copy" id="ink-confirm-copy">
            {ask.body}
          </div>
        ) : null}
        {ask.rows?.length ? (
          <div className="ink-modal-rows">
            {ask.rows.map((r) => (
              <div key={r.label} className="ink-modal-row">
                <span>{r.label}</span>
                <span>{r.text}</span>
              </div>
            ))}
          </div>
        ) : null}
        {ask.note ? <div className="ink-modal-note">{ask.note}</div> : null}
        {ask.foot ? <div className="ink-modal-foot">{ask.foot}</div> : null}
        {failed && ask.run ? (
          <div className="ink-modal-error" role="alert">
            {ask.run.failure}
          </div>
        ) : null}
        <div className="ink-modal-actions">
          <button
            type="button"
            className="ink-btn"
            autoFocus={ask.danger}
            disabled={running}
            onClick={() => answer(false)}
          >
            {ask.cancelLabel || 'Cancel'}
          </button>
          <button
            ref={confirmButton}
            type="button"
            className={ask.danger ? 'ink-btn ink-btn-danger' : 'ink-btn ink-btn-primary'}
            autoFocus={!ask.danger}
            disabled={running}
            onClick={() => void confirm()}
          >
            {running && ask.run
              ? ask.run.busyLabel
              : failed && ask.run
                ? ask.run.retryLabel
                : ask.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
